import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Trophy, BookOpen, Scroll, Feather, Flame, Lock, Award } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TreasurePage = () => {
  const { user, token } = useAuth();
  const [trophies, setTrophies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrophies();
  }, []);

  const fetchTrophies = async () => {
    try {
      const res = await fetch(`${API}/trophies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTrophies(data);
      }
    } catch (err) {
      console.error('Failed to fetch trophies:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTrophyIcon = (id) => {
    switch (id) {
      case 'drongo': return '🐦';
      case 'kingfisher': return '🦅';
      case 'owl': return '🦉';
      case 'robin': return '🐤';
      default: return '🏆';
    }
  };

  const getCategoryIcon = (type) => {
    switch (type) {
      case 'books': return <BookOpen className="w-5 h-5" />;
      case 'quests': return <Scroll className="w-5 h-5" />;
      case 'words': return <Feather className="w-5 h-5" />;
      case 'streak': return <Flame className="w-5 h-5" />;
      default: return <Trophy className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (type) => {
    // Use theme colors for all categories to maintain consistency
    return ['var(--th-md)', 'var(--th-dk)'];
  };

  const getTierStyle = (tier, earned) => {
    if (!earned) return { background: 'var(--border)', color: 'var(--text-light)' };
    switch (tier) {
      case 'gold': return { background: 'linear-gradient(135deg, #FCD34D, #F59E0B)', color: '#78350F' };
      case 'silver': return { background: 'linear-gradient(135deg, #E5E7EB, #9CA3AF)', color: '#374151' };
      case 'bronze': return { background: 'linear-gradient(135deg, #FDBA74, #EA580C)', color: '#7C2D12' };
      default: return { background: 'var(--primary)', color: 'white' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-4xl animate-bounce">🏆</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up" data-testid="treasure-page">
      {/* Page Hero */}
      <div 
        className="rounded-3xl p-6 text-center"
        style={{ 
          background: `linear-gradient(135deg, var(--th-md), var(--th-dk))`,
          color: 'white'
        }}
      >
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
            <Trophy className="w-6 h-6" />
          </div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fredoka' }}>
            Treasure Chest
          </h1>
        </div>
        <p className="opacity-90">Every book earns leaf points and unlocks trophies! 🏆</p>
      </div>

      {/* Trophy Shelf - Large Display */}
      <div 
        className="rounded-3xl p-8"
        style={{ 
          background: 'linear-gradient(135deg, var(--th-dk), var(--th-md), var(--th-lt))',
          color: 'white'
        }}
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="text-3xl">🏆</span>
          <div>
            <h2 className="font-bold text-2xl" style={{ fontFamily: 'Fredoka' }}>TROPHY SHELF</h2>
            <p className="text-sm opacity-90">Your greatest achievements</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {trophies.map(trophy => (
            <div
              key={trophy.id}
              data-testid={`trophy-${trophy.id}`}
              className="text-center"
            >
              {/* Large Circular Trophy Badge */}
              <div 
                className={`w-32 h-32 md:w-40 md:h-40 rounded-full mx-auto mb-4 flex items-center justify-center text-5xl md:text-6xl transition-all ${
                  trophy.earned ? 'shadow-2xl' : 'opacity-40'
                }`}
                style={trophy.earned 
                  ? getTierStyle(trophy.tier, trophy.earned)
                  : { background: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)' }
                }
              >
                {trophy.earned ? getTrophyIcon(trophy.id) : <Lock className="w-12 h-12 opacity-50" />}
              </div>
              
              {/* Trophy Name */}
              <div className="font-bold text-base md:text-lg mb-1">
                {trophy.name}
              </div>
              
              {/* Status */}
              <div className={`text-sm ${trophy.earned ? 'text-yellow-300' : 'text-white/60'}`}>
                {trophy.earned ? '✓ Earned' : 'Locked'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Achievement Progress Cards */}
      <div className="space-y-4">
        {trophies.map(trophy => {
          const [color1, color2] = getCategoryColor(trophy.type);
          const progress = Math.min((trophy.current / trophy.target) * 100, 100);
          
          return (
            <div
              key={trophy.id}
              data-testid={`achieve-${trophy.id}`}
              className="rounded-2xl p-4 border-2 flex items-center gap-4"
              style={{ 
                background: 'var(--card)',
                borderColor: 'var(--border)'
              }}
            >
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${color1}, ${color2})`, color: 'white' }}
              >
                {getCategoryIcon(trophy.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-bold" style={{ color: 'var(--text)' }}>
                  {trophy.name}'s {trophy.type === 'books' ? 'Books' : 
                   trophy.type === 'quests' ? 'Quest' :
                   trophy.type === 'words' ? 'Words' : 'Streak'}
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-light)' }}>
                  {trophy.type.charAt(0).toUpperCase() + trophy.type.slice(1)} · {trophy.current} of {trophy.target} to unlock
                </p>
                
                <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${color1}, ${color2})` }}
                  />
                </div>
                
                <p className="text-xs mt-1" style={{ color: 'var(--text-light)' }}>
                  🌿 Next: {trophy.target} {trophy.type} → {trophy.name} trophy
                </p>
              </div>
              
              <div className="text-center flex-shrink-0">
                <div className="text-2xl font-bold" style={{ color: color1 }}>
                  {trophy.current}
                </div>
                <div className="text-xs" style={{ color: 'var(--text-light)' }}>
                  {trophy.type}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Leaf Points Summary */}
      <div 
        className="rounded-2xl p-6 text-center"
        style={{ background: 'var(--background)' }}
      >
        <div className="text-4xl mb-2">🍃</div>
        <div className="text-3xl font-bold" style={{ color: 'var(--primary)' }}>
          {user?.leaf_points || 0}
        </div>
        <div className="text-sm" style={{ color: 'var(--text-light)' }}>
          Total Leaf Points
        </div>
      </div>
    </div>
  );
};

export default TreasurePage;
